import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import type { LibSidebar } from '../sidebar/lib-sidebar';
import css from './lib-sidebar-item.css';

/** Sidebar visuals with a projected routing link or a native fallback anchor. */
@customElement('lib-sidebar-item')
export class LibSidebarItem extends LibElement {
  protected override get hostFocusSelector(): string { return this.link ? '' : 'a[href]'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property({ attribute: 'image-url' }) imageUrl = '';
  @property() label = '';
  @property() href = '';
  @property() alignment = '';
  @property({ attribute: 'label-mode' }) labelMode = '';
  @property({ attribute: 'badge-number', type: Number }) badgeNumber: number | null = null;
  @property({ type: Boolean }) active = false;
  @state() private linkActive = false;
  private link: HTMLAnchorElement | null = null;
  private readonly observer = new MutationObserver(() => this.syncActive());

  private syncActive() { this.linkActive = this.link?.classList.contains('active') ?? false; }

  private bindLink() {
    this.observer.disconnect();
    const previousLink = this.link;
    const slot = this.renderRoot.querySelector<HTMLSlotElement>('slot[name="link"]');
    this.link = slot?.assignedElements().find((element): element is HTMLAnchorElement => element instanceof HTMLAnchorElement) ?? null;
    if (this.link) this.observer.observe(this.link, { attributes: true, attributeFilter: ['class'] });
    this.syncActive();
    if (previousLink !== this.link) this.requestUpdate();
  }

  override connectedCallback() {
    super.connectedCallback();
    void this.updateComplete.then(() => { if (this.isConnected) this.bindLink(); });
  }

  override disconnectedCallback() {
    this.observer.disconnect();
    super.disconnectedCallback();
  }

  override render() {
    const sidebar = this.closest<LibSidebar>('lib-sidebar');
    const alignment = this.alignment || sidebar?.alignment || 'left';
    const showLabel = this.labelMode ? this.labelMode !== 'hidden' : !sidebar?.hideLabels;
    const active = this.link ? this.linkActive : this.active;
    const badgeText = this.badgeNumber !== null && this.badgeNumber > 9 ? '9+' : this.badgeNumber;
    const builtInIcon = /(?:^|\/)_content\/WAGO\.SolutionsPlatform\.UILibrary\.Core\/wago-icons-svg\/[^/?#]+\.svg(?:[?#]|$)/.test(this.imageUrl);
    const iconStyle = `--icon-url: url(${JSON.stringify(this.imageUrl)})`;
    return html`<div class="item ${active ? 'active' : ''} align-${alignment}">
      <div class="visual" aria-hidden="true">
        ${this.badgeNumber !== null ? html`<span class="badge">${badgeText}</span>` : nothing}
        ${builtInIcon
          ? html`<span class="nav-link-img icon" style=${iconStyle}></span>`
          : html`<img class="nav-link-img" src=${this.imageUrl} alt="">`}
        <p class="nav-link-text" ?hidden=${!showLabel}>${this.label}</p>
      </div>
      <slot name="link" @slotchange=${this.bindLink}>
        <a href=${this.href} aria-label=${this.label} aria-current=${active ? 'page' : nothing}
         title=${showLabel ? nothing : this.label}></a>
      </slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-sidebar-item': LibSidebarItem; }
}
